/**
 * The dragons' poses on the title and story screens: continuous functions of time, at the display's
 * rate (the owner's standing note: smooth, not sprite-stepped). Idle breathing, a blink every few
 * seconds on each dragon's own schedule, and, in the story's bubbles, arms paddling to the C64's own
 * beat: its two pictures swap every 8 frames, a 16-frame (0.32 s) cycle (storyText.ts).
 */

import type { Puppet } from '../actors/puppet';

/** Mean time between blinks, seconds. */
const BLINK_PERIOD = 3.3;
/** How long one blink takes, closing and opening, seconds. */
const BLINK_SECONDS = 0.18;
/** The C64's paddle cycle in the story: two pictures of 8 frames each at 50 Hz. */
export const PADDLE_SECONDS = 16 / 50;

/** A number in [0, 1) from two integers: a fixed scatter, the same every run. */
function scatter(k: number, seed: number): number {
  let h = Math.imul(k ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(seed + 0x632be5ab, 0xc2b2ae35);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 0) / 0x100000000;
}

function lid(t: number, start: number): number {
  const u = (t - start) / BLINK_SECONDS;
  if (u <= 0 || u >= 1) return 0;
  const x = 1 - Math.abs(2 * u - 1);
  return x * x * (3 - 2 * x);
}

/**
 * How far a dragon's eyes are shut at `t` seconds (0 open, 1 shut): one blink in each 3.3 s slot at
 * a scattered moment, every fourth a double blink. `seed` gives each dragon its own schedule.
 */
export function blinkAt(t: number, seed: number): number {
  const slot = Math.floor(t / BLINK_PERIOD);
  let shut = 0;
  for (let k = slot - 1; k <= slot; k++) {
    if (k < 0) continue;
    const start = (k + 0.15 + scatter(k, seed) * 0.7) * BLINK_PERIOD;
    shut = Math.max(shut, lid(t, start));
    if (k % 4 === 3) shut = Math.max(shut, lid(t, start + BLINK_SECONDS + 0.08));
  }
  return shut;
}

function eyes(pup: Puppet, shut: number): void {
  const sy = 1 - 0.88 * shut;
  pup.part('eyeNear').scale.set(1, sy, 1);
  pup.part('eyeFar').scale.set(1, sy, 1);
}

/** Standing idle, facing `dir`: breathing, the head nodding a touch, blinking. */
export function poseIdle(pup: Puppet, t: number, dir: 1 | -1, seed: number): void {
  pup.face(dir);
  pup.part('mouth').visible = false;
  const breathe = Math.sin(t * Math.PI * 2 * 0.9 + seed) * 0.025;
  pup.pose('footNear');
  pup.pose('footFar');
  pup.pose('body', 0, breathe).rotation.z = Math.sin(t * Math.PI * 2 * 0.45 + seed) * 0.02;
  pup.pose('head', 0, breathe * 0.5).rotation.z = Math.sin(t * Math.PI * 2 * 0.3 + seed * 2) * 0.05;
  pup.pose('armNear', 0, breathe * 0.6);
  pup.pose('armFar', 0, breathe * 0.6);
  eyes(pup, blinkAt(t, seed));
}

/**
 * Floating in a bubble (the story): feet dangling, arms paddling on the C64's 0.32 s beat; `phase`
 * 0 or 1 puts the two dragons in opposite phase, as the C64 does.
 */
export function poseFloating(pup: Puppet, t: number, dir: 1 | -1, seed: number, phase: 0 | 1): void {
  pup.face(dir);
  pup.part('mouth').visible = false;
  const a = (t / PADDLE_SECONDS + phase * 0.5) * Math.PI * 2;
  const paddle = Math.sin(a);
  pup.pose('footNear', 0.04, -0.04 + Math.max(0, paddle) * 0.04).rotation.z = -0.25;
  pup.pose('footFar', -0.02, -0.02 + Math.max(0, -paddle) * 0.04).rotation.z = -0.2;
  pup.pose('body', 0, 0).rotation.z = 0.04 + paddle * 0.03;
  pup.pose('head').rotation.z = 0.06 - paddle * 0.02;
  // Hands out to the sides, flapping up and down, as in the C64's pictures.
  pup.pose('armNear', -0.14, 0.06 + paddle * 0.12, 0.18).rotation.x = -0.4 - paddle * 0.3;
  pup.pose('armFar', -0.14, 0.06 + paddle * 0.12, -0.18).rotation.x = 0.4 + paddle * 0.3;
  eyes(pup, blinkAt(t, seed));
}
